import React, { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Badge,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Home as HomeIcon,
  People as PeopleIcon,
  SupportAgent as SupportAgentIcon,
  Chat as ChatIcon,
  AttachMoney as DespesasIcon,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';

import { useAuth, usePermissions } from '../contexts/AuthContext';
import { useAtendimentoNotification } from '../contexts/AtendimentoNotificationContext';
import {
  getMobileFooterBarSx,
  getMobileFooterFabSx,
  getMobileFooterFabSlotSx,
  getMobileFooterNavButtonSx,
  getMobileFooterShellSx,
  getMobileFooterSideSx,
} from './mobileFooterMenuStyles';

interface MobileNavItem {
  id: string;
  label: string;
  icon: SvgIconComponent;
  path: string;
  enabled: boolean;
  isActive: (pathname: string) => boolean;
  showBadge?: boolean;
  badgeContent?: number;
}

const MobileFooterMenu: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { can } = usePermissions();
  const { badgeCount, temNovidade } = useAtendimentoNotification();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';

  const profile = user?.perfil?.toLowerCase() || '';
  const hasClientAccess = !user || ['administrador', 'financeiro', 'suporte'].includes(profile);
  const hasDespesasAccess = !user || ['administrador', 'financeiro', 'suporte'].includes(profile);
  const hasAtendimentoAccess = can('atendimento.tickets.acessar');

  const leftItems = useMemo<MobileNavItem[]>(
    () => [
      {
        id: 'clients',
        label: 'Clientes',
        icon: PeopleIcon,
        path: '/clients',
        enabled: hasClientAccess,
        isActive: (pathname) => pathname === '/clients' || pathname.startsWith('/clients/'),
      },
      {
        id: 'despesas',
        label: 'Despesas',
        icon: DespesasIcon,
        path: '/contas-a-pagar',
        enabled: hasDespesasAccess,
        isActive: (pathname) =>
          pathname.startsWith('/contas-a-pagar') ||
          pathname.startsWith('/despesas'),
      },
    ],
    [hasClientAccess, hasDespesasAccess],
  );

  const rightItems = useMemo<MobileNavItem[]>(
    () => [
      {
        id: 'chamados',
        label: 'Chamados',
        icon: SupportAgentIcon,
        path: '/chamados',
        enabled: true,
        isActive: (pathname) =>
          pathname.startsWith('/chamados') || pathname.startsWith('/tickets'),
      },
      {
        id: 'atendimento',
        label: 'Atendimento',
        icon: ChatIcon,
        path: '/atendimento/atendimentos',
        enabled: hasAtendimentoAccess,
        isActive: (pathname) => pathname.startsWith('/atendimento/atendimentos'),
        showBadge: hasAtendimentoAccess && temNovidade,
        badgeContent: badgeCount,
      },
    ],
    [hasAtendimentoAccess, temNovidade, badgeCount],
  );

  const isDashboardActive =
    location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/');

  const handleNavigate = (path: string, enabled: boolean) => {
    if (!enabled) return;
    navigate(path);
  };

  const renderNavItem = (item: MobileNavItem) => {
    const active = item.isActive(location.pathname);
    const Icon = item.icon;

    const iconNode = item.showBadge ? (
      <Badge
        badgeContent={item.badgeContent}
        color="error"
        max={99}
        overlap="circular"
        sx={{
          '& .MuiBadge-badge': {
            fontSize: '0.6rem',
            height: 16,
            minWidth: 16,
            fontWeight: 700,
          },
        }}
      >
        <Icon sx={{ fontSize: '1.35rem' }} />
      </Badge>
    ) : (
      <Icon sx={{ fontSize: '1.35rem' }} />
    );

    return (
      <Box
        key={item.id}
        component="button"
        type="button"
        aria-label={item.label}
        aria-current={active ? 'page' : undefined}
        disabled={!item.enabled}
        onClick={() => handleNavigate(item.path, item.enabled)}
        sx={getMobileFooterNavButtonSx(theme, isDark, active, !item.enabled)}
      >
        <Box className="nav-icon-wrap">{iconNode}</Box>
        <Typography component="span" className="nav-label">
          {item.label}
        </Typography>
      </Box>
    );
  };

  if (!isMobile) {
    return null;
  }

  return (
    <Box component="nav" aria-label="Navegação principal" sx={getMobileFooterShellSx(theme, isDark)}>
      <Box sx={getMobileFooterBarSx(theme, isDark)}>
        <Box sx={getMobileFooterSideSx()}>{leftItems.map(renderNavItem)}</Box>

        <Box sx={getMobileFooterFabSlotSx()} aria-hidden />

        <Box
          component="button"
          type="button"
          aria-label="Início"
          aria-current={isDashboardActive ? 'page' : undefined}
          onClick={() => navigate('/dashboard')}
          sx={getMobileFooterFabSx(theme, isDark, isDashboardActive)}
        >
          <HomeIcon />
        </Box>

        <Box sx={getMobileFooterSideSx()}>{rightItems.map(renderNavItem)}</Box>
      </Box>
    </Box>
  );
};

export default MobileFooterMenu;
